import { useState, type ReactNode } from 'react';
import type { Scope } from '../../api/types/permissions';
import type { CreateTenantUserRequestLocal } from '../../api/types/TenantLocal';
import type { PrincipalIdentifierLocal } from '../../api/types/PlatformLocal';
import type { ScopedUserApi } from './scopeDescriptors';
import { generateIdempotencyKey } from '../../lib/idempotency';
import { identifierBody } from './identifierFields';
import { LevelEntityPicker, isGenericLevel } from './LevelEntityPicker';
import { Label } from '../ui/label';
import { SELECT_CLASS } from '../../lib/selectClass';
import { PLATFORM_SCOPE_NAME } from '../../lib/labelConstants';
import type { LabelResolver } from '../../hooks/useLabelResolver';

/** Devuelve el mapa solo si tiene al menos una clave; si está vacío/ausente → undefined (se omite del body). */
function emptyToUndefined(
    map: Record<string, string> | undefined,
): Record<string, string> | undefined {
    return map && Object.keys(map).length > 0 ? map : undefined;
}

/** Values que la estrategia necesita para crear. `email`/`phone` ambos string (RHF deja `''` el no-primario). */
export interface DestinationFormValues {
    email: string;
    phone: string;
    displayName: string;
    roleId: string;
    /** Campos personalizados del tenant (mapa key→value). Vacío si no hay catálogo. */
    customFields?: Record<string, string>;
}

/** Resultado de un submit. */
export interface DestinationSubmitResult {
    /** Total de destinos que el flujo intentó (siempre 1 en el flujo tenant). */
    totalRefs: number;
    /** Asignaciones EXTRA que fallaron (vacío en el flujo tenant). */
    failedExtra: PromiseRejectedResult[];
    /** Si el éxito multi-destino debe mostrar el toast "en N …" (siempre false hoy). */
    multiSuccess: boolean;
    /** Etiqueta del toast multi-destino exitoso. */
    multiLabel: string;
    /** Etiqueta del toast de fallo parcial. */
    failLabel: string;
}

export interface DestinationStrategy {
    /** Bloque de UI con el selector de destino correcto para el scope del rol. */
    DestinationSelector: ReactNode;
    /** ¿El submit está habilitado? (hay destino válido según el nivel del rol). */
    canSubmit: boolean;
    /** Resetea el estado interno del selector — se llama cuando cambia el rol elegido. */
    resetSelection: () => void;
    /** Destinos de la asignación para el scope del rol (entidades de nivel o el ref fijo). */
    resolveScopeRefs: (roleScope: Scope) => string[];
    /** Ejecuta la creación. */
    submit: (
        values: DestinationFormValues,
        roleScope: Scope,
        principalIdentifier: PrincipalIdentifierLocal,
    ) => Promise<DestinationSubmitResult>;
}

interface Args {
    /** scopeRef del contexto (el tenantId). */
    scopeRef: string;
    /** tenantId destino — los roles y las asignaciones van por este tenant (== scopeRef). */
    tid: string;
    /** Scope REAL del rol elegido (de listRoles). null si no hay rol. */
    roleScope: Scope | null;
    /** ¿Hay un rol elegido? */
    hasRole: boolean;
    /** Alta de usuario del scope (tenantApi.createUser / platformApi.createTenantUser), inyectada por el descriptor. */
    createUser: ScopedUserApi['createUser'];
    /** Etiqueta readonly del destino TENANT: el nombre de la organización. */
    tenantDestLabel: string;
    /** Resolvedor del tenant destino; da los nombres de nivel del picker. */
    labels?: LabelResolver;
}

/**
 * Estrategia de DESTINO + creación de usuario. El destino se deriva del scope REAL del rol elegido:
 * TENANT → el propio tenant (fijo); PLATFORM → '*' (fijo). Los roles de niveles bajo el tenant
 * (LEVEL_N) se asignan por las pantallas de nivel genérico, no por este alta. El binding de creación
 * (`createUser`) y la etiqueta del destino los inyecta el descriptor → sirve al flujo tenant y al
 * drill-down platform con la MISMA lógica.
 */
export function useDestinationStrategy({
    scopeRef,
    tid,
    roleScope,
    hasRole,
    createUser,
    tenantDestLabel,
    labels,
}: Args): DestinationStrategy {
    // scopeRef efectivo: TENANT → tid; PLATFORM → '*'.
    const fixedRef = roleScope === 'PLATFORM' ? '*' : tid;

    // DEC-027: destino MÚLTIPLE de un rol de nivel genérico (LEVEL_1/2/3) = entidades de ese nivel.
    const [levelRefs, setLevelRefs] = useState<string[]>([]);
    const isLevel = isGenericLevel(roleScope);

    const canSubmit =
        hasRole &&
        (roleScope === 'TENANT' ||
            roleScope === 'PLATFORM' ||
            // LEVEL_N: habilitado solo con ≥1 entidad de nivel elegida.
            (isLevel && levelRefs.length > 0));

    const resetSelection = () => {
        // Limpia la sub-selección de nivel (se llama al cambiar de rol).
        setLevelRefs([]);
    };

    const DestinationSelector =
        roleScope === 'PLATFORM' ? (
            <ReadonlyDest id="uf-dest-platform" label="Destino" value={PLATFORM_SCOPE_NAME} />
        ) : roleScope === 'TENANT' ? (
            <ReadonlyDest id="uf-dest-tenant" label="Destino" value={tenantDestLabel} />
        ) : isGenericLevel(roleScope) ? (
            // `key={roleScope}` remonta el picker al cambiar de nivel → reinicia su cadena de padres.
            <LevelEntityPicker
                key={roleScope}
                tenantId={tid}
                level={roleScope}
                value={levelRefs}
                onChange={setLevelRefs}
                idPrefix="uf-lvl"
                labels={labels}
            />
        ) : null;

    const submit = async (
        values: DestinationFormValues,
        scope: Scope,
        principalIdentifier: PrincipalIdentifierLocal,
    ): Promise<DestinationSubmitResult> => {
        const customFields = emptyToUndefined(values.customFields);
        const scopeIsLevel = isGenericLevel(scope);
        const body: CreateTenantUserRequestLocal = {
            // DEC-RBAC-079/081: email/phone/primaryIdentifier según el identificador PRIMARIO del tenant.
            ...identifierBody(values, principalIdentifier),
            displayName: values.displayName,
            roleIds: [values.roleId],
            scope,
            // DEC-027: LEVEL_N → scopeRef apunta a la 1ª entidad elegida (campo requerido del contrato) y
            // `scopeRefs` lleva TODAS (el backend crea 1 asignación por id). TENANT/PLATFORM → ref fijo.
            scopeRef: scopeIsLevel ? (levelRefs[0] ?? fixedRef) : fixedRef,
            scopeRefs: scopeIsLevel ? levelRefs : undefined,
            customFields,
        };
        await createUser(tid, body, { idempotencyKey: generateIdempotencyKey() });
        void scopeRef;
        const total = scopeIsLevel ? levelRefs.length : 1;
        return {
            totalRefs: total,
            failedExtra: [],
            // Un solo request crea todas las asignaciones server-side → sin toast multi-destino.
            multiSuccess: false,
            multiLabel: 'destinos',
            failLabel: 'destino(s)',
        };
    };

    const resolveScopeRefs = (scope: Scope): string[] =>
        isGenericLevel(scope) ? levelRefs : [scope === 'PLATFORM' ? '*' : tid];

    return { DestinationSelector, canSubmit, resetSelection, resolveScopeRefs, submit };
}

function ReadonlyDest({ id, label, value }: { id: string; label: string; value: string }) {
    return (
        <div className="space-y-1">
            <Label htmlFor={id}>{label}</Label>
            <input id={id} className={SELECT_CLASS} value={value} readOnly disabled />
        </div>
    );
}
